import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Loader2, AlertTriangle, CheckCircle2, ExternalLink } from "lucide-react";

const INDIGO = "#6366f1";
const DARK_BG = "#05070a";
const MUTED = "#94a3b8";
const CARD_BORDER = "rgba(99,102,241,0.15)";

interface BillingStatus {
  tier: string;
  displayName: string;
  subscriptionStatus: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  maxActiveCandidates: number | null;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" });
}

export default function SettingsBilling() {
  const [, navigate] = useLocation();
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [successDismissed, setSuccessDismissed] = useState(false);
  const [currency, setCurrency] = useState<"nzd" | "aud">("nzd");

  const searchParams = new URLSearchParams(window.location.search);
  const showSuccess = searchParams.get("success") === "true" && !successDismissed;

  useEffect(() => {
    fetch("/api/billing/status")
      .then((r) => r.json())
      .then((data) => { setStatus(data); setLoading(false); })
      .catch(() => { setError("Failed to load billing status"); setLoading(false); });
  }, []);

  useEffect(() => {
    if (!showSuccess) return;
    const timer = setTimeout(() => setSuccessDismissed(true), 5000);
    return () => clearTimeout(timer);
  }, [showSuccess]);

  async function handleUpgrade(planKey: string) {
    setActionLoading(planKey);
    try {
      const res = await apiRequest("POST", "/api/billing/checkout", { planKey, billingCycle: "monthly", currency });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else setError(data.error ?? "Failed to start checkout");
    } catch {
      setError("Failed to start checkout");
    } finally {
      setActionLoading(null);
    }
  }

  async function handlePortal() {
    setActionLoading("portal");
    try {
      const res = await apiRequest("POST", "/api/billing/portal");
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else setError(data.error ?? "Failed to open billing portal");
    } catch {
      setError("Failed to open billing portal");
    } finally {
      setActionLoading(null);
    }
  }

  return (
    <div style={{ background: DARK_BG, minHeight: "100vh", color: "#f1f5f9", fontFamily: "Inter, sans-serif" }}>
      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/agent/dashboard")}
            data-testid="billing-back"
            style={{ color: MUTED }}
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold">Billing</h1>
        </div>

        {/* Settings nav */}
        <div className="flex gap-4 mb-8 border-b pb-4" style={{ borderColor: CARD_BORDER }}>
          <button
            onClick={() => navigate("/settings/communications")}
            className="text-sm px-3 py-1.5 rounded-md transition-colors"
            style={{ color: MUTED }}
            data-testid="nav-communications"
          >
            Communications
          </button>
          <button
            className="text-sm px-3 py-1.5 rounded-md font-medium"
            style={{ color: "#f1f5f9", background: "rgba(99,102,241,0.12)", border: `1px solid ${CARD_BORDER}` }}
            data-testid="nav-billing"
          >
            Billing
          </button>
        </div>

        {/* Success banner */}
        {showSuccess && status && (
          <div
            className="flex items-center gap-3 rounded-lg px-5 py-4 mb-6"
            style={{ background: "rgba(34,197,94,0.10)", border: "1px solid rgba(34,197,94,0.3)" }}
            data-testid="billing-success-banner"
          >
            <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0" />
            <span className="text-green-300 text-sm">
              You're now on the <strong>{status.displayName}</strong> plan. Your workspace has been upgraded.
            </span>
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-3 py-16 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: MUTED }} />
            <span style={{ color: MUTED }}>Loading billing status…</span>
          </div>
        )}

        {error && (
          <div className="rounded-lg px-5 py-4 mb-6" style={{ background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.3)" }}>
            <p className="text-red-300 text-sm" data-testid="billing-error">{error}</p>
          </div>
        )}

        {status && !loading && (
          <>
            {/* FREE TIER */}
            {status.subscriptionStatus === "free" && (
              <div data-testid="billing-free-state">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                  <div>
                    <p className="text-lg font-semibold">Your current plan: <span style={{ color: INDIGO }}>Free</span></p>
                    <p className="text-sm mt-1" style={{ color: MUTED }}>
                      Up to {status.maxActiveCandidates ?? 10} active candidates. Upgrade to unlock more.
                    </p>
                  </div>

                  {/* Currency toggle */}
                  <div
                    className="inline-flex self-start sm:self-auto rounded-full p-1 gap-1 shrink-0"
                    style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${CARD_BORDER}` }}
                    data-testid="billing-currency-toggle"
                  >
                    {(["nzd", "aud"] as const).map(c => (
                      <button
                        key={c}
                        onClick={() => setCurrency(c)}
                        data-testid={`billing-currency-${c}`}
                        className="px-4 py-1 rounded-full text-xs font-semibold transition-all duration-200"
                        style={
                          currency === c
                            ? { background: INDIGO, color: "#ffffff" }
                            : { background: "transparent", color: MUTED }
                        }
                      >
                        {c === "nzd" ? "🇳🇿 NZD" : "🇦🇺 AUD"}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Starter */}
                  <div className="rounded-xl p-5 flex flex-col gap-4" style={{ border: `1px solid ${CARD_BORDER}`, background: "rgba(255,255,255,0.03)" }} data-testid="plan-card-starter">
                    <div>
                      <p className="font-semibold">Starter</p>
                      <p className="text-sm mt-0.5" style={{ color: MUTED }}>
                        {currency === "nzd" ? "$99 NZD / month" : "$89 AUD / month"}
                      </p>
                    </div>
                    <ul className="text-xs space-y-1" style={{ color: MUTED }}>
                      <li>Up to 50 active candidates</li>
                      <li>Your own sending domain</li>
                      <li>SMS notifications</li>
                    </ul>
                    <Button
                      className="w-full mt-auto"
                      style={{ background: INDIGO, color: "#fff" }}
                      disabled={actionLoading === "starter"}
                      onClick={() => handleUpgrade("starter")}
                      data-testid="upgrade-starter"
                    >
                      {actionLoading === "starter" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upgrade to Starter"}
                    </Button>
                  </div>

                  {/* Professional */}
                  <div className="rounded-xl p-5 flex flex-col gap-4" style={{ border: `1px solid rgba(99,102,241,0.4)`, background: "rgba(99,102,241,0.05)" }} data-testid="plan-card-professional">
                    <div>
                      <p className="font-semibold">Professional</p>
                      <p className="text-sm mt-0.5" style={{ color: MUTED }}>
                        {currency === "nzd" ? "$299 NZD / month" : "$269 AUD / month"}
                      </p>
                    </div>
                    <ul className="text-xs space-y-1" style={{ color: MUTED }}>
                      <li>Up to 200 active candidates</li>
                      <li>Custom email template editing</li>
                      <li>Optional Vexis footer removal</li>
                    </ul>
                    <Button
                      className="w-full mt-auto"
                      style={{ background: INDIGO, color: "#fff" }}
                      disabled={actionLoading === "professional"}
                      onClick={() => handleUpgrade("professional")}
                      data-testid="upgrade-professional"
                    >
                      {actionLoading === "professional" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upgrade to Professional"}
                    </Button>
                  </div>

                  {/* Enterprise */}
                  <div className="rounded-xl p-5 flex flex-col gap-4" style={{ border: `1px solid ${CARD_BORDER}`, background: "rgba(255,255,255,0.03)" }} data-testid="plan-card-enterprise">
                    <div>
                      <p className="font-semibold">Enterprise</p>
                      <p className="text-sm mt-0.5" style={{ color: MUTED }}>Contact us</p>
                    </div>
                    <ul className="text-xs space-y-1" style={{ color: MUTED }}>
                      <li>Unlimited active candidates</li>
                      <li>Full white label</li>
                      <li>SLA support agreement</li>
                    </ul>
                    <Button
                      variant="outline"
                      className="w-full mt-auto"
                      style={{ borderColor: CARD_BORDER, color: MUTED }}
                      onClick={() => navigate("/contact")}
                      data-testid="upgrade-enterprise"
                    >
                      Enquire Now
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ACTIVE SUBSCRIPTION */}
            {status.subscriptionStatus === "active" && (
              <div data-testid="billing-active-state">
                <div className="rounded-xl p-6" style={{ border: `1px solid ${CARD_BORDER}`, background: "rgba(255,255,255,0.03)" }}>
                  <p className="text-lg font-semibold mb-1">
                    Your current plan: <span style={{ color: INDIGO }}>{status.displayName}</span>
                  </p>
                  <p className="text-sm" style={{ color: MUTED }}>
                    Next billing date: <strong style={{ color: "#f1f5f9" }}>{formatDate(status.currentPeriodEnd)}</strong>
                  </p>

                  {status.cancelAtPeriodEnd && (
                    <div className="mt-4 flex items-start gap-3 rounded-lg px-4 py-3" style={{ background: "rgba(234,179,8,0.10)", border: "1px solid rgba(234,179,8,0.3)" }} data-testid="cancel-notice">
                      <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
                      <p className="text-sm text-yellow-300">
                        Your subscription cancels on {formatDate(status.currentPeriodEnd)}. Reactivate anytime in the billing portal.
                      </p>
                    </div>
                  )}

                  <Button
                    className="mt-5"
                    style={{ background: INDIGO, color: "#fff" }}
                    disabled={actionLoading === "portal"}
                    onClick={handlePortal}
                    data-testid="manage-billing"
                  >
                    {actionLoading === "portal" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                    Manage billing
                  </Button>
                </div>
              </div>
            )}

            {/* PAST DUE */}
            {status.subscriptionStatus === "past_due" && (
              <div data-testid="billing-past-due-state">
                <div className="flex items-start gap-3 rounded-lg px-5 py-4 mb-6" style={{ background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.3)" }}>
                  <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-300">
                    Your last payment failed. Update your payment method to restore full access.
                  </p>
                </div>
                <Button
                  style={{ background: INDIGO, color: "#fff" }}
                  disabled={actionLoading === "portal"}
                  onClick={handlePortal}
                  data-testid="update-payment"
                >
                  {actionLoading === "portal" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Update payment method
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
